import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '..';
import { EventInput } from '@fullcalendar/core';
import { CalendarEventStatusEnum } from 'src/enums/ECalendarEvents';

export interface CalendarStats {
	totalCalls: number;
	meetings: number;
	pending: number;
}

export interface CalendarEventsState {
	events: EventInput[];
	activeDateEvents: EventInput[];
	activeDate: string;
	loading: boolean;
	error: string | null;
	stats: CalendarStats;
}

const initialState: CalendarEventsState = {
	events: [],
	activeDateEvents: [],
	activeDate: '',
	loading: false,
	error: null,
	stats: { totalCalls: 0, meetings: 0, pending: 0 },
};

const calendarEventsSlice = createSlice({
	name: 'calendarEvents',
	initialState,
	reducers: {
		setEvents(state, action: PayloadAction<EventInput[]>) {
			state.events = action.payload;
			const totalCalls = action.payload.length;
			const meetings = action.payload.filter(
				(ev) =>
					ev.status === CalendarEventStatusEnum.CONFIRMED ||
					ev.status === CalendarEventStatusEnum.SCHEDULED,
			).length;
			const pending = action.payload.filter(
				(ev) => ev.status === CalendarEventStatusEnum.RESCHEDULED,
			).length;
			state.stats = { totalCalls, meetings, pending };
		},
		setActiveDate(state, action: PayloadAction<string>) {
			state.activeDate = action.payload;
			if (state.events.length > 0) {
				state.activeDateEvents = state.events.filter((ev) => {
					if (!ev.start) {
						return false;
					}
					const eventDate = ev.start.toString().slice(0, 10);
					return eventDate === action.payload;
				});
			}
		},
		setLoading(state, action: PayloadAction<boolean>) {
			state.loading = action.payload;
		},
		setError(state, action: PayloadAction<string | null>) {
			state.error = action.payload;
		},
		setStats(state, action: PayloadAction<CalendarStats>) {
			state.stats = action.payload;
		},
	},
});

export const { setEvents, setActiveDate, setLoading, setError, setStats } =
	calendarEventsSlice.actions;
export default calendarEventsSlice.reducer;

export const selectCalendarEvents = (state: RootState) => state.calendarEvents.events;
export const selectActiveDateCalendarStats = (state: RootState) =>
	state.calendarEvents.activeDateEvents;
export const selectActiveDate = (state: RootState) => state.calendarEvents.activeDate;
export const selectCalendarLoading = (state: RootState) => state.calendarEvents.loading;
export const selectCalendarError = (state: RootState) => state.calendarEvents.error;
export const selectCalendarStats = (state: RootState) => state.calendarEvents.stats;
